import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { fmtMoney } from '../lib/format';
import { publicUrl, thumbUrl } from '../lib/fileUrl';
import SignaturePad from '../components/SignaturePad';

// ═══════════════════════════════════════════════════════════════════════════
//  THE PAGE A CUSTOMER OPENS
//
//  A homeowner taps a link in a text message. They have no account, no
//  password, and no idea what QPS Hub is. What they want to know is three
//  things: who is this from, what is the price, and do I say yes.
//
//  So the page is one column, big type, and one button that matters. No
//  navigation, no sign-in, no app chrome — there is nowhere else to go from
//  here and offering somewhere is how you lose an approval.
//
//  ── IT IS THE TECHNICIAN'S DOCUMENT, NOT OURS ───────────────────────────
//  The letterhead is their business and their logo. QPS Hub appears once, in
//  grey, at the very bottom. The price shown is the technician's own price:
//  the server's allowlist (server/share.js) never sends the markup, so there
//  is nothing on this page that could leak it even by accident.
//
//  ── WHY IT IS NOT PART OF THE APP ────────────────────────────────────────
//  index.js renders this INSTEAD of App when the path is /d/..., so none of
//  the session restore, brand theming or screen machinery runs. A stranger's
//  quote cannot be broken by a change to the technician portal, and a page
//  with no auth code in it cannot accidentally grow some.
// ═══════════════════════════════════════════════════════════════════════════

const docDate = (d) => {
  const date = d ? new Date(d) : null;
  if (!date || isNaN(date)) return '';
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
};

// Only http(s), and never a bare "joist.com/x" which the browser would read as
// a page on this site.
const href = (v) => {
  const raw = String(v || '').trim();
  if (!raw) return '';
  try {
    const u = new URL(/^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`);
    return ['http:', 'https:'].includes(u.protocol) ? u.href : '';
  } catch { return ''; }
};

const NOTE = /^\s*(note|nota)\s*:\s*/i;

// Every newline in a description is its own line, exactly as the technician
// typed it. "Note:" sets underneath in italic.
function Scope({ description }) {
  const rows = String(description || '').split('\n').map(r => r.trim()).filter(Boolean);
  if (!rows.length) return null;
  return (
    <div className="pub-scope">
      {rows.map((r, i) => (
        NOTE.test(r)
          ? <p key={i} className="pub-scope-note">{r.replace(NOTE, '')}</p>
          : <p key={i}>{r}</p>
      ))}
    </div>
  );
}

// Photos the technician attached to a line. Shown, not listed — somebody
// deciding on $900 of work wants to see the pipe.
function Photos({ photos = [] }) {
  const shots = photos.map(publicUrl).filter(Boolean);
  if (!shots.length) return null;
  return (
    <div className="pub-shots">
      {shots.map((url, i) => (
        <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="pub-shot">
          <img src={thumbUrl(photos[i]) || url} alt="" loading="lazy" />
        </a>
      ))}
    </div>
  );
}

export default function PublicDoc({ kind, token }) {
  const [doc, setDoc] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);

  // The answer, once they give one.
  const [answering, setAnswering] = useState('');   // '' | 'accept' | 'decline'
  const [name, setName] = useState('');
  const [signature, setSignature] = useState('');
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState('');
  const [settled, setSettled] = useState(null);     // { decision, message }

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await axios.get(`/api/public/${kind}/${token}`);
      setDoc(res.data);
      setName(res.data?.to?.name || '');
    } catch (err) {
      setLoadError(err.response?.data?.message
        || 'This document could not be opened. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [kind, token]);

  useEffect(() => { load(); }, [load]);

  // The tab title is the only branding a customer sees before the page paints.
  useEffect(() => {
    if (doc?.from?.name) {
      document.title = `${doc.kind === 'invoice' ? 'Invoice' : 'Estimate'} from ${doc.from.name}`;
    }
  }, [doc]);

  const label = kind === 'invoice' ? 'Invoice' : 'Estimate';

  const answer = async (accept) => {
    setProblem('');
    const who = name.trim();
    if (!who) { setProblem('Type your name.'); return; }
    if (accept && !signature) { setProblem('Sign in the box above the button.'); return; }
    setSending(true);
    try {
      const res = await axios.post(`/api/public/${kind}/${token}/decision`, {
        accept, name: who, signatureImage: accept ? signature : '', reason: accept ? '' : reason,
      });
      setSettled({ decision: res.data.decision, message: res.data.message });
    } catch (err) {
      setProblem(err.response?.data?.message || 'That did not go through. Try once more.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="pub-wrap"><div className="pub-state">Opening…</div></div>;
  }

  if (loadError) {
    return (
      <div className="pub-wrap">
        <div className="pub-state">
          <h1 className="pub-state-h">Link not valid</h1>
          <p>{loadError}</p>
          <button type="button" className="pub-retry" onClick={load}>Try again</button>
        </div>
        <PubFoot />
      </div>
    );
  }

  const decided = settled?.decision || doc.decision;
  const canDecide = doc.canDecide && !settled;

  return (
    <div className="pub-wrap">
      <div className="pub-sheet print-doc">
        <div className="pub-kind">{label.toUpperCase()}</div>

        <header className="pub-head">
          <div className="pub-from">
            {doc.from.logoUrl && <img className="pub-logo" src={doc.from.logoUrl} alt="" />}
            <div className="pub-from-n">{doc.from.name}</div>
            {doc.from.address && <div>{doc.from.address}</div>}
            {doc.from.phone && <div>{doc.from.phone}</div>}
            {doc.from.email && <div>{doc.from.email}</div>}
          </div>
          {doc.to.name && (
            <div className="pub-to">
              <div className="pub-to-l">Prepared For</div>
              <div className="pub-to-n">{doc.to.name}</div>
              {doc.to.address && <div>{doc.to.address}</div>}
            </div>
          )}
        </header>

        <div className="pub-mid">
          {doc.serviceAddress ? (
            <div className="pub-service">
              <div className="pub-service-l">Service Address</div>
              <div className="pub-service-v">{doc.serviceAddress}</div>
            </div>
          ) : <div />}
          <div className="pub-meta">
            <div><span>{label} #</span><b>{doc.number || '—'}</b></div>
            <div><span>Date</span><b>{docDate(doc.date)}</b></div>
          </div>
        </div>

        {/* On a phone each line is its own block with its price on the right.
            A four-column table at 360px turns "Replace water heater" into six
            words stacked one per row, which is how a customer stops reading. */}
        <div className="pub-lines">
          {doc.lines.length === 0 ? (
            <div className="pub-none">Nothing has been listed on this {label.toLowerCase()}.</div>
          ) : doc.lines.map((l, i) => (
            <div key={i} className="pub-line">
              <div className="pub-line-top">
                <div className="pub-line-n">{l.name || 'Work'}</div>
                <div className="pub-line-t">{fmtMoney(l.total)}</div>
              </div>
              <Scope description={l.description} />
              {l.qty !== 1 && (
                <div className="pub-line-rate">{l.qty} × {fmtMoney(l.unitCost)}</div>
              )}
              <Photos photos={l.photos} />
              {(l.files || []).length > 0 && (
                <div className="pub-files">
                  {l.files.map((f, n) => {
                    const at = publicUrl(f.url);
                    return at
                      ? <a key={n} href={at} target="_blank" rel="noopener noreferrer">{f.name || 'Attachment'}</a>
                      // Named rather than linked: a reader still needs to know
                      // a report backs this line, and a dead link is worse
                      // than no link.
                      : <span key={n}>{f.name || 'Attachment'}</span>;
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="pub-totals">
          {doc.subtotal !== doc.total && (
            <div className="pub-total-row"><span>Subtotal</span><span>{fmtMoney(doc.subtotal)}</span></div>
          )}
          <div className="pub-total-row grand"><span>Total</span><span>{fmtMoney(doc.total)}</span></div>
          {doc.kind === 'invoice' && doc.paid > 0 && (
            <>
              <div className="pub-total-row"><span>Paid</span><span>{fmtMoney(doc.paid)}</span></div>
              <div className="pub-total-row grand"><span>Balance Due</span><span>{fmtMoney(doc.balance)}</span></div>
            </>
          )}
        </div>

        {doc.notes && (
          <section className="pub-notes">
            <div className="pub-notes-l">Notes</div>
            <div className="pub-notes-b">{doc.notes}</div>
          </section>
        )}

        {(href(doc.externalUrl) || doc.documents.length > 0) && (
          <section className="pub-notes">
            <div className="pub-notes-l">Attached</div>
            {href(doc.externalUrl) && (
              <div className="pub-attach">
                <a href={href(doc.externalUrl)} target="_blank" rel="noopener noreferrer">{doc.externalUrl}</a>
              </div>
            )}
            {doc.documents.map((f, i) => (
              <div key={i} className="pub-attach">
                <a href={publicUrl(f.url)} target="_blank" rel="noopener noreferrer">{f.name || 'Attachment'}</a>
              </div>
            ))}
          </section>
        )}
      </div>

      {/* ── The answer ──────────────────────────────────────────────────── */}

      {decided && (
        <div className={`pub-done ${decided === 'accepted' ? 'yes' : 'no'}`}>
          <div className="pub-done-h">
            {decided === 'accepted' ? 'Approved' : 'Declined'}
          </div>
          <p>
            {settled?.message
              || (decided === 'accepted'
                ? `${doc.decidedByName || 'You'} approved this on ${docDate(doc.decidedAt)}.`
                : `This was declined on ${docDate(doc.decidedAt)}.`)}
          </p>
          <button type="button" className="pub-print" onClick={() => window.print()}>
            Save a copy
          </button>
        </div>
      )}

      {canDecide && (
        <div className="pub-decide print-hide">
          <div className="pub-ask">Do you approve this estimate for {fmtMoney(doc.total)}?</div>

          <label className="pub-field">
            <span>Your name</span>
            <input value={name} onChange={e => setName(e.target.value)}
              placeholder="First and last name" autoComplete="name" />
          </label>

          {answering !== 'decline' && (
            <div className="pub-sign">
              <div className="pub-sign-l">Sign below</div>
              <SignaturePad onChange={setSignature} />
              <p className="pub-agree">{doc.agreement}</p>
            </div>
          )}

          {answering === 'decline' && (
            <label className="pub-field">
              <span>Anything you want them to know? (optional)</span>
              <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
                placeholder="Too much, bad timing, going with someone else…" />
            </label>
          )}

          {problem && <div className="pub-problem">{problem}</div>}

          {answering === 'decline' ? (
            <>
              <button type="button" className="pub-btn no" disabled={sending}
                onClick={() => answer(false)}>
                {sending ? 'Sending…' : 'Send my answer'}
              </button>
              <button type="button" className="pub-link"
                onClick={() => { setAnswering(''); setProblem(''); }}>
                Go back
              </button>
            </>
          ) : (
            <>
              <button type="button" className="pub-btn yes" disabled={sending}
                onClick={() => answer(true)}>
                {sending ? 'Sending…' : 'Approve this estimate'}
              </button>
              <button type="button" className="pub-link"
                onClick={() => { setAnswering('decline'); setProblem(''); }}>
                No thanks
              </button>
            </>
          )}
        </div>
      )}

      {/* An invoice asks nothing. It gives them a way to keep it. */}
      {doc.kind === 'invoice' && (
        <div className="pub-decide print-hide">
          <div className="pub-ask">
            {doc.balance > 0
              ? `${fmtMoney(doc.balance)} due to ${doc.from.name}.`
              : 'This invoice is paid in full.'}
          </div>
          {doc.from.phone && (
            <a className="pub-btn yes" href={`tel:${doc.from.phone.replace(/[^\d+]/g, '')}`}>
              Call {doc.from.name}
            </a>
          )}
          <button type="button" className="pub-link" onClick={() => window.print()}>
            Save a copy
          </button>
        </div>
      )}

      <PubFoot />
    </div>
  );
}

// The only place QPS Hub puts its own name on somebody else's document.
function PubFoot() {
  return (
    <footer className="pub-foot print-hide">
      Sent with <a href="/" target="_blank" rel="noopener noreferrer">QPS Hub</a>
      {' '}— free estimates and invoices for trades.
    </footer>
  );
}
